import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Share2, Facebook, Twitter, MessageCircle, Link2, Download, 
  Copy, AlertTriangle, MapPin, Calendar, Phone, Check
} from 'lucide-react';
import { motion } from 'framer-motion';

export const ShareFlyer = ({ caseData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const flyerRef = useRef(null);

  // Social-share URL must hit `/api/c/{id}` so crawler previews land on
  // the per-case SSR meta-tag route (Emergent's ingress only forwards
  // `/api/*` to the backend; bare `/case/{id}` serves the static SPA
  // shell). The route 302-redirects humans back to `/case/{id}`.
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/c/${caseData.id}`
    : '';

  const shareText = `🚨 MISSING: ${caseData.name}${caseData.age ? `, ${caseData.age} years old` : ''}. Last seen at ${caseData.last_seen_location}. Please share to help bring them home! #MissingPerson #HelpFind`;

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const placeholderImages = [
    'https://images.pexels.com/photos/27349684/pexels-photo-27349684.jpeg',
  ];

  const getImage = () => {
    if (caseData.photo_base64) {
      return caseData.photo_base64.startsWith('data:') 
        ? caseData.photo_base64 
        : `data:image/jpeg;base64,${caseData.photo_base64}`;
    }
    return placeholderImages[0];
  };

  const shareToFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareToTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareToWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
      '_blank'
    );
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const downloadFlyer = async () => {
    // Create a canvas-based flyer for download
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 800;
    canvas.height = 1000;

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Header bar
    ctx.fillStyle = '#F97316';
    ctx.fillRect(0, 0, canvas.width, 80);

    // "MISSING" text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('MISSING', canvas.width / 2, 55);

    // Urgent badge if applicable
    if (caseData.is_urgent) {
      ctx.fillStyle = '#EF4444';
      ctx.fillRect(canvas.width - 120, 90, 110, 30);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'right';
      ctx.fillText('⚠ URGENT', canvas.width - 15, 112);
    }

    // Load and draw image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = getImage();
    
    await new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });

    // Photo placeholder/image
    const imgX = 100;
    const imgY = 100;
    const imgW = 600;
    const imgH = 400;
    
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(imgX, imgY, imgW, imgH);
    
    try {
      ctx.drawImage(img, imgX, imgY, imgW, imgH);
    } catch {
      ctx.fillStyle = '#94a3b8';
      ctx.font = '24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Photo Not Available', canvas.width / 2, imgY + imgH / 2);
    }

    // Name
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 36px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(caseData.name, canvas.width / 2, 550);

    // Age/Gender
    ctx.font = '24px Arial';
    ctx.fillStyle = '#64748b';
    let ageGender = [];
    if (caseData.age) ageGender.push(`${caseData.age} years old`);
    if (caseData.gender) ageGender.push(caseData.gender);
    ctx.fillText(ageGender.join(' • '), canvas.width / 2, 585);

    // Details section
    ctx.textAlign = 'left';
    let y = 640;

    // Last Seen Location
    ctx.fillStyle = '#F97316';
    ctx.font = 'bold 18px Arial';
    ctx.fillText('📍 Last Seen Location:', 50, y);
    ctx.fillStyle = '#0f172a';
    ctx.font = '18px Arial';
    ctx.fillText(caseData.last_seen_location, 50, y + 25);
    y += 70;

    // Last Seen Date
    ctx.fillStyle = '#0EA5E9';
    ctx.font = 'bold 18px Arial';
    ctx.fillText('📅 Last Seen Date:', 50, y);
    ctx.fillStyle = '#0f172a';
    ctx.font = '18px Arial';
    ctx.fillText(formatDate(caseData.last_seen_date), 50, y + 25);
    y += 70;

    // Physical Features if available
    if (caseData.physical_features) {
      ctx.fillStyle = '#8B5CF6';
      ctx.font = 'bold 18px Arial';
      ctx.fillText('👤 Description:', 50, y);
      ctx.fillStyle = '#0f172a';
      ctx.font = '16px Arial';
      
      // Word wrap description
      const words = caseData.physical_features.split(' ');
      let line = '';
      let lineY = y + 25;
      for (const word of words) {
        const testLine = line + word + ' ';
        if (ctx.measureText(testLine).width > 700) {
          ctx.fillText(line.trim(), 50, lineY);
          line = word + ' ';
          lineY += 22;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line.trim(), 50, lineY);
      y = lineY + 40;
    }

    // Contact info
    ctx.fillStyle = '#22C55E';
    ctx.font = 'bold 18px Arial';
    ctx.fillText('📞 Contact:', 50, y);
    ctx.fillStyle = '#0f172a';
    ctx.font = '18px Arial';
    ctx.fillText(caseData.contact_info, 50, y + 25);

    // Footer
    ctx.fillStyle = '#F97316';
    ctx.fillRect(0, canvas.height - 60, canvas.width, 60);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('LinkTheMissing.org • Share to Help!', canvas.width / 2, canvas.height - 25);

    // Download
    const link = document.createElement('a');
    link.download = `missing-${caseData.name.replace(/\s+/g, '-').toLowerCase()}-flyer.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    
    toast.success('Flyer downloaded!');
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Missing: ${caseData.name}`,
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Share failed');
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          onClick={nativeShare}
          className="bg-trust-blue-500 hover:bg-trust-blue-600 text-white rounded-full gap-2"
          data-testid="share-case-btn"
        >
          <Share2 className="w-4 h-4" />
          Share Case
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Share2 className="w-5 h-5 text-trust-blue-500" />
            Share This Case
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Flyer Preview */}
          <Card className="overflow-hidden" ref={flyerRef}>
            <div className="bg-safety-orange-500 py-3 px-4 flex items-center justify-between">
              <span className="text-white font-bold text-lg">MISSING</span>
              {caseData.is_urgent && (
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> URGENT
                </span>
              )}
            </div>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <img 
                  src={getImage()} 
                  alt={caseData.name}
                  className="w-24 h-24 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg truncate">{caseData.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {caseData.age && `${caseData.age} yrs`}
                    {caseData.age && caseData.gender && ' • '}
                    {caseData.gender}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                    <MapPin className="w-3 h-3 text-safety-orange-500" />
                    <span className="truncate">{caseData.last_seen_location}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Calendar className="w-3 h-3 text-trust-blue-500" />
                    <span>{formatDate(caseData.last_seen_date)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Share Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <Button 
              onClick={shareToFacebook}
              className="bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-xl h-12"
              data-testid="share-facebook"
            >
              <Facebook className="w-5 h-5 mr-2" />
              Facebook
            </Button>
            <Button 
              onClick={shareToTwitter}
              className="bg-[#1DA1F2] hover:bg-[#1A94DA] text-white rounded-xl h-12"
              data-testid="share-twitter"
            >
              <Twitter className="w-5 h-5 mr-2" />
              Twitter
            </Button>
            <Button 
              onClick={shareToWhatsApp}
              className="bg-[#25D366] hover:bg-[#22C55E] text-white rounded-xl h-12"
              data-testid="share-whatsapp"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp
            </Button>
            <Button 
              onClick={copyLink}
              variant="outline"
              className="rounded-xl h-12"
              data-testid="copy-link"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 mr-2 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Link2 className="w-5 h-5 mr-2" />
                  Copy Link
                </>
              )}
            </Button>
          </div>

          {/* Download Flyer */}
          <Button 
            onClick={downloadFlyer}
            variant="outline"
            className="w-full rounded-xl h-12 border-2 border-dashed"
            data-testid="download-flyer"
          >
            <Download className="w-5 h-5 mr-2" />
            Download Printable Flyer
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Every share helps bring missing persons home. Thank you for caring!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
